import classNames from 'classnames';
import { showToast } from 'components/Toast';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { DashboardScreen, trackEvent } from 'helpers/mixpanelEvents';
import { FC, useEffect, useRef } from 'react';
import ReactDatetime from 'react-datetime';
import { getUserDates } from 'selectors/viewPreferences';
import { userDates } from 'store/viewPreferences/slice';

const RANGE_SELECTED_DEBOUNCE_MS = 800;

interface Props {
  centered?: boolean;
  screen?: DashboardScreen;
}

export const CustomDateFilter: FC<Props> = ({ centered, screen }) => {
  const dispatch = useAppDispatch();
  const dateRange = useAppSelector(getUserDates);

  // Convert ISO strings to Date objects for react-datetime
  const startDate = dateRange.start ? new Date(dateRange.start) : new Date();
  const endDate = dateRange.end ? new Date(dateRange.end) : new Date();

  // Debounced on dateRange rather than tracked inline in handleChangeDates, so a start-then-end
  // pick (two separate setDateRange calls) collapses into a single event with the final range.
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (!screen) return;

    const timeoutId = setTimeout(() => {
      trackEvent('Custom Date Range Selected', {
        screen,
        start_date: dateRange.start,
        end_date: dateRange.end,
      });
    }, RANGE_SELECTED_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [dateRange.start, dateRange.end, screen]);

  const handleChangeDates = (type: string, item) => {
    // Convert Moment object to Date if needed
    let date: Date;
    if (item && typeof item.toDate === 'function') {
      // It's a Moment object
      date = item.toDate();
    } else if (item instanceof Date) {
      // It's already a Date object
      date = item;
    } else {
      // It's a string or other format
      date = new Date(item);
    }

    if (type === 'start') {
      const newStartDate = dayjs(date).startOf('day').toDate();

      // Validate: start date must be before or equal to end date
      if (newStartDate > endDate) {
        showToast('Start date cannot be after end date', 'error');
        return;
      }

      dispatch(userDates({ start: newStartDate.toISOString(), end: endDate.toISOString() }));
    } else {
      const newEndDate = dayjs(date).endOf('day').toDate();

      // Validate: end date must be after or equal to start date
      if (newEndDate < startDate) {
        showToast('End date cannot be before start date', 'error');
        return;
      }

      dispatch(userDates({ start: startDate.toISOString(), end: newEndDate.toISOString() }));
    }
  };

  return (
    <div
      className={classNames('date-filters-container', {
        'justify-center': centered,
      })}
    >
      <div className="date-column" data-testid="mock-datetime">
        <span className="body-small-text">Start Date</span>
        <form>
          <ReactDatetime
            inputProps={{
              className: 'form-control picker-input',
              placeholder: 'Select Start Date',
              readOnly: true,
            }}
            onChange={e => handleChangeDates('start', e)}
            value={startDate}
            closeOnSelect
            timeFormat={false}
            data-testid="start-picker"
          />
        </form>
      </div>
      <div className="date-column">
        <span className="body-small-text">End Date</span>
        <form>
          <ReactDatetime
            inputProps={{
              className: 'form-control picker-input',
              placeholder: 'Select End Date',
              readOnly: true,
            }}
            onChange={e => handleChangeDates('end', e)}
            value={endDate}
            closeOnSelect
            timeFormat={false}
            data-testid="end-picker"
          />
        </form>
      </div>
    </div>
  );
};
